import { ChangeEvent, useCallback, useMemo, useState } from 'react'

import { SelectChangeEvent } from '@mui/material'

import { isUnknownGenre } from '../helpers/tagColumns'
import { ObjectId, TrackedGame } from '../types'
import { useUATaxonomy } from './useUATaxonomy'

export interface GenreOption {
  id: ObjectId
  name: string
}

export interface GamesListingFilter {
  search: string
  genreFilter: string
  setGenreFilter: (value: string) => void
  /** Genres present in `games`, deduped by id and sorted by display name. */
  availableGenres: GenreOption[]
  filteredGames: TrackedGame[]
  handleSearchChange: (event: ChangeEvent<HTMLInputElement>) => void
  handleGenreChange: (event: SelectChangeEvent<string>) => void
}

/**
 * Name search + genre dropdown state for the tracked-game listing dialogs. The
 * filter stores a genre id, so the all-games listing can seed it from the
 * dashboard's `genreFilter` URL param.
 *
 * Dropdown options are derived from `games` rather than the full taxonomy, so
 * the user can never pick a genre that would render an empty table.
 */
export const useGamesListingFilter = (games: TrackedGame[], initialGenreFilter = ''): GamesListingFilter => {
  const [search, setSearch] = useState('')
  const [genreFilter, setGenreFilter] = useState(initialGenreFilter)
  const { genreLabel } = useUATaxonomy()

  const handleSearchChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value)
  }, [])

  const handleGenreChange = useCallback((event: SelectChangeEvent<string>) => {
    setGenreFilter(event.target.value)
  }, [])

  const availableGenres = useMemo(() => {
    const seen = new Map<string, string>()
    games.forEach((game) => {
      if (isUnknownGenre(game.genre)) return
      if (!seen.has(game.genre.id)) seen.set(game.genre.id, genreLabel(game.genre))
    })
    return Array.from(seen.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name))
  }, [games, genreLabel])

  const filteredGames = useMemo(() => {
    const trimmed = search.trim().toLowerCase()
    return games.filter((game) => {
      if (genreFilter && game.genre.id !== genreFilter) return false
      if (trimmed && !game.name.toLowerCase().includes(trimmed)) return false
      return true
    })
  }, [games, search, genreFilter])

  return { search, genreFilter, setGenreFilter, availableGenres, filteredGames, handleSearchChange, handleGenreChange }
}
